/**
 * The mini timer in the top bar — spec 01 §8's "Mini timer trên top bar", and the
 * reason `TopBar` has carried a named, empty slot since Task 41.
 *
 * It is a **reader**, not an owner. Everything it shows comes from `useTimer()`, so
 * it cannot disagree with the panel on `/time-tracking`: they are the same object,
 * rendered twice. See `TimerProvider.tsx` for why that is structural rather than a
 * convention, and `web/shapes/time-tracking.md` §5 for the decision.
 *
 * It is not a read-only display either. Pause and stop are here because the whole
 * point of a timer that crosses pages is that the person can stop it from wherever
 * they are — a mini timer you have to navigate away from to stop is a reminder, not
 * a control. Starting is not here: starting needs a task, and choosing one is a
 * search box that does not belong in a top bar.
 *
 * Four things it must not do, each of which it would be easy to do:
 *
 *  - **Say "no timer" while it does not know.** Until the first read answers, the
 *    slot says "đang tải". `status === 'error'` says the read failed and offers a
 *    retry. Neither is rendered as "Chưa có hẹn giờ nào đang chạy", because an
 *    employee who reads that starts a second timer on top of a first one that is
 *    still accruing.
 *  - **Render a zero.** With no live entry there is no elapsed time, so there is no
 *    number — not `0:00:00`.
 *  - **Blink.** `DESIGN.md` §9: nothing pulses. The seconds change; nothing else does.
 *  - **Hide the validation state.** A `pending_validation` timer says so here as
 *    well as on the page, because that is where the person is looking.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useT } from '../i18n';
import { isRetryable } from '../api/errors';
import { Icon } from '../states/icons';
import { RetryAction } from '../states/RetryAction';
import { ValidationBadge } from './badges';
import { formatClock, LONG_RUNNING_SECONDS } from './elapsed';
import { useTimer } from './TimerProvider';

export function MiniTimer(): ReactNode {
  const t = useT();
  const { status, entry, error, busy, elapsedSeconds, pause, resume, stop, reload } = useTimer();

  if (status === 'loading') {
    return (
      <span className="wp-minitimer" data-testid="mini-timer-loading">
        <Icon name="clock" />
        {t('timer.mini.loading')}
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className="wp-minitimer wp-minitimer--error" data-testid="mini-timer-error">
        <Icon name="circle-alert" />
        {t('timer.mini.unreadable')}
        {error !== null && isRetryable(error) ? (
          <RetryAction onRetry={reload} testId="mini-timer-retry" />
        ) : null}
      </span>
    );
  }

  if (entry === null) {
    return (
      <span className="wp-minitimer" data-testid="mini-timer-idle">
        <Icon name="clock" />
        <Link to="/time-tracking">{t('timer.mini.none')}</Link>
      </span>
    );
  }

  const running = entry.status === 'running';
  const title = entry.task_title ?? entry.achau_task_id ?? t('timer.task.unnamed');

  return (
    <span className="wp-minitimer" data-testid="mini-timer">
      <Icon name={running ? 'play' : 'pause'} />
      <Link to="/time-tracking" className="wp-minitimer__task">
        {title}
      </Link>
      {/* `role="timer"` is the ARIA role for exactly this, and `aria-live="off"` is
          deliberate: a value that changes every second announced every second is
          unusable. The number is read on demand, like a clock. */}
      <span className="wp-minitimer__clock wp-mono" role="timer" aria-live="off">
        <span className="wp-visually-hidden">{t('timer.elapsed')}: </span>
        {formatClock(elapsedSeconds)}
      </span>
      {entry.task_validation_state === 'validated' ? null : (
        <ValidationBadge state={entry.task_validation_state} testId="mini-timer-validation" />
      )}
      {running && elapsedSeconds >= LONG_RUNNING_SECONDS ? (
        <span className="wp-small wp-muted" data-testid="mini-timer-long">
          <Icon name="hourglass" /> {t('timer.longRunning.short')}
        </span>
      ) : null}
      <button
        type="button"
        className="wp-button"
        disabled={busy}
        onClick={() => {
          void (running ? pause() : resume());
        }}
        data-testid="mini-timer-toggle"
      >
        <Icon name={running ? 'pause' : 'play'} />
        {running ? t('timer.pause') : t('timer.resume')}
      </button>
      <button
        type="button"
        className="wp-button"
        disabled={busy}
        onClick={() => {
          void stop();
        }}
        data-testid="mini-timer-stop"
      >
        <Icon name="square" />
        {t('timer.stop')}
      </button>
    </span>
  );
}
